import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { X, ExternalLink, Loader2, Sparkles } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { Toaster } from 'react-hot-toast';
import { getPlayableVideoUrl } from '../utils/proxyMedia';

const formatNumber = (num) => {
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(0) + 'K';
  return num?.toString() || '0';
};

const getApifyValue = (video, field) => {
  if (video.apify_metadata) {
    const meta = typeof video.apify_metadata === 'string'
      ? JSON.parse(video.apify_metadata)
      : video.apify_metadata;
    const fieldMap = {
      views: ['playCount', 'videoPlayCount', 'videoViewCount', 'viewCount', 'views', 'view_count', 'views_count', 'video_view_count'],
      likes: ['diggCount', 'likeCount', 'likesCount', 'likes', 'like_count', 'likes_count'],
      comments: ['commentCount', 'commentsCount', 'comments', 'comment_count', 'comments_count'],
      shares: ['shareCount', 'shares', 'share_count', 'shares_count']
    };
    const keys = fieldMap[field] || [field];
    for (const key of keys) {
      if (meta[key] !== undefined && meta[key] !== null) return meta[key];
    }
  }
  const columnMap = { views: 'apify_views', likes: 'apify_likes', comments: 'apify_comments', shares: 'apify_shares' };
  return video[columnMap[field]] || 0;
};

const getCreatorUsername = (video) => {
  if (video.apify_metadata) {
    const meta = typeof video.apify_metadata === 'string' ? JSON.parse(video.apify_metadata) : video.apify_metadata;
    const username = meta.authorMeta?.uniqueId || meta.authorMeta?.name || meta.author?.username || meta.username;
    if (username) return username;
  }
  if (video.apify_author_username) return video.apify_author_username;
  const urlMatch = video.source_url?.match(/@([^\/\?]+)/);
  return urlMatch ? urlMatch[1] : 'unknown';
};

const getSoundUrl = (video) => {
  if (!video?.apify_metadata) return null;
  try {
    const meta = typeof video.apify_metadata === 'string' ? JSON.parse(video.apify_metadata) : video.apify_metadata;
    const platform = video.platform || 'tiktok';
    if (platform === 'tiktok' && meta.musicMeta?.musicId) {
      return `https://www.tiktok.com/music/Originalton-${meta.musicMeta.musicId}`;
    }
    if (platform === 'instagram') {
      const audioId = meta.musicInfo?.audio_id || meta.audio_id || meta.music?.audio_id || meta.musicInfo?.id;
      if (audioId) return `https://www.instagram.com/reels/audio/${audioId}`;
    }
    return null;
  } catch (e) {
    return null;
  }
};

const parseJsonField = (val) => {
  if (val == null) return {};
  if (typeof val === 'object') return val;
  try {
    return JSON.parse(val) || {};
  } catch (e) {
    return {};
  }
};

// Extract numeric trend id from URL param: "80" or "80-three-types-of-people" -> 80
function getTrendIdFromSlug(slugOrId) {
  if (!slugOrId) return null;
  const firstSegment = slugOrId.split('-')[0];
  const id = parseInt(firstSegment, 10);
  return Number.isNaN(id) ? null : id;
}

export default function SharedTrendDetail() {
  const { trendId: slugOrId } = useParams();
  const trendId = getTrendIdFromSlug(slugOrId);
  const { isDark } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);

  useEffect(() => {
    if (!trendId) {
      setError('Invalid share link.');
      setLoading(false);
      return;
    }
    let cancelled = false;
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await fetch(`/api/trend-spotter/share/${trendId}`);
        const json = await res.json();
        if (cancelled) return;
        if (json.success) {
          setData({ template: json.template, topVideos: json.topVideos || [] });
        } else {
          setError(json.error || 'Trend not found or not shareable.');
        }
      } catch (err) {
        if (!cancelled) setError('Failed to load shared trend.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [trendId]);

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-zinc-950' : 'bg-gray-50'}`}>
        <Loader2 className="h-8 w-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className={`min-h-screen flex items-center justify-center p-4 ${isDark ? 'bg-zinc-950 text-zinc-200' : 'bg-gray-50 text-gray-800'}`}>
        <div className="text-center max-w-md">
          <p className="mb-4">{error || 'Trend not found.'}</p>
          <Link
            to="/trend-spotter"
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg ${isDark ? 'bg-zinc-800 text-cyan-400 hover:bg-zinc-700' : 'bg-cyan-500 text-white hover:bg-cyan-600'}`}
          >
            Open ShowStop Trend Spotter
          </Link>
        </div>
      </div>
    );
  }

  const t = data.template;
  const hook = parseJsonField(t.hook);
  const template = parseJsonField(t.template);
  const difficulty = typeof t.difficulty_to_create === 'object' ? (t.difficulty_to_create?.score ?? 3) : 3;
  const hashtags = typeof t.optimization_cues === 'object' && Array.isArray(t.optimization_cues?.keyHashtags)
    ? t.optimization_cues.keyHashtags.join(' ')
    : (t.hashtags || '');
  const topVideos = data.topVideos || [];

  const soundUrls = { tiktok: null, instagram: null };
  topVideos.forEach((v) => {
    const url = getSoundUrl(v);
    if (url && v.platform === 'tiktok') soundUrls.tiktok = soundUrls.tiktok || url;
    if (url && v.platform === 'instagram') soundUrls.instagram = soundUrls.instagram || url;
  });

  const base = isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-gray-200';
  const text = isDark ? 'text-white' : 'text-gray-900';
  const muted = isDark ? 'text-zinc-400' : 'text-gray-600';
  const label = isDark ? 'text-zinc-500' : 'text-gray-600';

  return (
    <div className={`min-h-screen ${isDark ? 'bg-zinc-950' : 'bg-gray-100'}`}>
      <Toaster position="top-right" />
      {/* Header */}
      <div className={`sticky top-0 z-10 border-b ${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-gray-200'}`}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <span className={`text-sm ${muted}`}>Viewing shared trend</span>
          <Link
            to="/trend-spotter"
            className={`flex items-center gap-2 text-sm font-medium ${isDark ? 'text-cyan-400 hover:text-cyan-300' : 'text-cyan-600 hover:text-cyan-700'}`}
          >
            <ExternalLink className="h-4 w-4" />
            Open ShowStop Trend Spotter
          </Link>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Title & type */}
        <div className="mb-4">
          <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${isDark ? 'bg-blue-500/20 text-blue-300' : 'bg-blue-100 text-blue-800'}`}>
            {t.trend_type || 'Format'}
          </span>
          <h1 className={`text-2xl sm:text-3xl font-bold mt-2 ${text}`}>{t.trend_name}</h1>
          {t.trend_summary && <p className={`mt-2 text-sm ${muted}`}>{t.trend_summary}</p>}
          {Array.isArray(t.trend_elements) && t.trend_elements.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {t.trend_elements.map((el, i) => (
                <span key={i} className={`px-2 py-0.5 rounded-md text-xs font-medium ${isDark ? 'bg-cyan-500/20 text-cyan-300' : 'bg-cyan-100 text-cyan-800'}`}>{el}</span>
              ))}
            </div>
          )}
          <p className={`text-sm mt-1 ${muted}`}>{t.video_count || 0} saved videos</p>
        </div>

        {/* Top videos - above the fold so users see them without scrolling */}
        {topVideos.length > 0 && (
          <div className="mb-6">
            <h3 className={`text-sm font-medium uppercase tracking-wider ${muted} mb-3`}>Top performing videos</h3>
            <div className="flex gap-4 overflow-x-auto pb-2 -mx-4 sm:mx-0 px-4 sm:px-0">
              {topVideos.map((video) => {
                let videoUrl = video.video_url;
                if (!videoUrl && video.apify_metadata) {
                  const meta = typeof video.apify_metadata === 'string' ? JSON.parse(video.apify_metadata) : video.apify_metadata;
                  videoUrl = meta.videoUrl || meta.video_url || meta.mediaUrls?.[0] || meta.webVideoUrl;
                }
                const thumbUrl = video.cover_url || video.coverUrl || null;
                return (
                  <div key={video.id} className="flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => videoUrl && setVideoPreview({ videoUrl, creator: getCreatorUsername(video), platform: video.platform || 'tiktok', views: getApifyValue(video, 'views'), likes: getApifyValue(video, 'likes') })}
                      className={`relative w-24 h-32 rounded-lg overflow-hidden ${isDark ? 'bg-zinc-800' : 'bg-gray-100'} hover:ring-2 ring-cyan-500/50 transition-all cursor-pointer`}
                    >
                      {thumbUrl ? (
                        <img
                          src={thumbUrl}
                          alt=""
                          className="w-full h-full object-cover absolute inset-0"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            const fallback = e.target.nextElementSibling;
                            if (fallback) fallback.style.display = 'block';
                          }}
                        />
                      ) : null}
                      <div className="absolute inset-0 w-full h-full" style={{ display: thumbUrl ? 'none' : 'block' }}>
                        {videoUrl ? (
                          <video
                            src={getPlayableVideoUrl(videoUrl)}
                            className="w-full h-full object-cover"
                            muted
                            loop
                            playsInline
                            onMouseEnter={(e) => e.target.play().catch(() => {})}
                            onMouseLeave={(e) => { e.target.pause(); e.target.currentTime = 0; }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-xs text-zinc-500">No video</div>
                        )}
                      </div>
                      <div className="absolute top-1 left-1 z-10">
                        {video.platform === 'instagram' ? (
                          <div className="w-5 h-5 bg-gradient-to-br from-purple-500 to-pink-500 rounded flex items-center justify-center text-[9px] text-white">IG</div>
                        ) : (
                          <div className="w-5 h-5 bg-black rounded flex items-center justify-center text-[9px] text-white">TT</div>
                        )}
                      </div>
                    </button>
                    <p className={`mt-1 text-xs truncate w-24 ${muted}`}>@{getCreatorUsername(video)}</p>
                    <p className={`text-xs ${muted}`}>{formatNumber(getApifyValue(video, 'views'))} views</p>
                  </div>
                );
              })}
            </div>
            <div className={`mt-4 pt-4 border-t ${isDark ? 'border-zinc-700/50' : 'border-gray-200'}`}>
              {user ? (
                <Link
                  to={`/trend-spotter?trendId=${trendId}`}
                  className={`inline-flex items-center gap-2 px-4 py-3 rounded-xl font-semibold text-sm transition-all ${isDark ? 'bg-cyan-500/20 text-cyan-300 hover:bg-cyan-500/30 border border-cyan-500/40' : 'bg-cyan-500 text-white hover:bg-cyan-600 shadow-lg shadow-cyan-500/25'}`}
                >
                  <Sparkles className="h-4 w-4" />
                  Create a viral video with this trend
                </Link>
              ) : (
                <button
                  type="button"
                  onClick={() => navigate('/login', { state: { from: { pathname: '/trend-spotter', search: `?trendId=${trendId}` } } })}
                  className={`inline-flex items-center gap-2 px-4 py-3 rounded-xl font-semibold text-sm transition-all ${isDark ? 'bg-cyan-500/20 text-cyan-300 hover:bg-cyan-500/30 border border-cyan-500/40' : 'bg-cyan-500 text-white hover:bg-cyan-600 shadow-lg shadow-cyan-500/25'}`}
                >
                  <Sparkles className="h-4 w-4" />
                  Log in to create a viral video with this trend
                </button>
              )}
            </div>
          </div>
        )}

        {/* Hook + Template side-by-side on large screens to shorten page */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Hook */}
          <div className={`${base} rounded-2xl p-5 sm:p-6 border`}>
            <div className="flex items-center gap-2 mb-4">
              <span className="text-xl">🚀</span>
              <h3 className={`text-sm font-medium uppercase tracking-wider ${muted}`}>The Hook</h3>
            </div>
            <div className="space-y-3">
              <div>
                <label className={`${label} text-xs mb-1 block`}>Visual</label>
                <p className={`text-sm ${text}`}>{hook.visual || '—'}</p>
              </div>
              <div>
                <label className={`${label} text-xs mb-1 block`}>Text Overlay</label>
                <p className={`text-sm ${text}`}>{hook.text_overlay || '—'}</p>
              </div>
              <div>
                <label className={`${label} text-xs mb-1 block`}>Sound</label>
                <p className={`text-sm ${text}`}>{hook.sound || '—'}</p>
                {(soundUrls.tiktok || soundUrls.instagram) && (
                  <div className="flex gap-2 flex-wrap mt-2">
                    {soundUrls.tiktok && (
                      <a href={soundUrls.tiktok} target="_blank" rel="noopener noreferrer" className="text-xs text-cyan-500 hover:text-cyan-400 underline">
                        🎵 TikTok Sound
                      </a>
                    )}
                    {soundUrls.instagram && (
                      <a href={soundUrls.instagram} target="_blank" rel="noopener noreferrer" className="text-xs text-cyan-500 hover:text-cyan-400 underline">
                        🎵 Instagram Sound
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Template */}
          <div className={`${base} rounded-2xl p-5 sm:p-6 border`}>
            <div className="flex items-center gap-2 mb-4">
              <span className="text-xl">📋</span>
              <h3 className={`text-sm font-medium uppercase tracking-wider ${muted}`}>Template</h3>
            </div>
            <div className="space-y-3">
              <div>
                <label className={`${label} text-xs mb-1 block`}>Visual Structure</label>
                <p className={`text-sm ${text} whitespace-pre-wrap`}>{template.visual || '—'}</p>
              </div>
              <div>
                <label className={`${label} text-xs mb-1 block`}>Text Pattern</label>
                <p className={`text-sm ${text} whitespace-pre-wrap`}>{template.text_overlay || '—'}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Why it works, Difficulty, Hashtags - single row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className={`${base} rounded-2xl p-6 border`}>
            <div className="flex items-center gap-2 mb-4">
              <span className="text-xl">💡</span>
              <h3 className={`text-sm font-medium uppercase tracking-wider ${muted}`}>Why It Works</h3>
            </div>
            <p className={`text-sm ${text}`}>{t.why_it_works || '—'}</p>
          </div>
          <div className={`${base} rounded-2xl p-6 border`}>
            <div className="flex items-center gap-2 mb-4">
              <span className="text-xl">⭐</span>
              <h3 className={`text-sm font-medium uppercase tracking-wider ${muted}`}>Difficulty</h3>
            </div>
            <p className={`text-sm ${text}`}>
              {['', 'Very Easy', 'Easy', 'Medium', 'Hard', 'Very Hard'][difficulty]}
            </p>
          </div>
          <div className={`${base} rounded-2xl p-6 border`}>
            <div className="flex items-center gap-2 mb-4">
              <span className="text-xl">#</span>
              <h3 className={`text-sm font-medium uppercase tracking-wider ${muted}`}>Hashtags</h3>
            </div>
            <p className={`text-sm ${text}`}>{hashtags || '—'}</p>
          </div>
        </div>

        {/* How to Use + Suggested Captions (MASTER DOC fields) */}
        {(t.how_to_use || (typeof t.optimization_cues === 'object' && Array.isArray(t.optimization_cues?.suggestedCaptions) && t.optimization_cues.suggestedCaptions.length > 0)) && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            {t.how_to_use && (
              <div className={`${base} rounded-2xl p-6 border`}>
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xl">📌</span>
                  <h3 className={`text-sm font-medium uppercase tracking-wider ${muted}`}>How to Use</h3>
                </div>
                <p className={`text-sm ${text}`}>{t.how_to_use}</p>
              </div>
            )}
            {typeof t.optimization_cues === 'object' && Array.isArray(t.optimization_cues?.suggestedCaptions) && t.optimization_cues.suggestedCaptions.length > 0 && (
              <div className={`${base} rounded-2xl p-6 border`}>
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xl">💬</span>
                  <h3 className={`text-sm font-medium uppercase tracking-wider ${muted}`}>Suggested Captions</h3>
                </div>
                <ul className={`space-y-2 text-sm ${text}`}>
                  {t.optimization_cues.suggestedCaptions.map((cap, i) => (
                    <li key={i} className="flex gap-2">
                      <span className={`flex-shrink-0 ${label}`}>{i + 1}.</span>
                      <span>"{cap}"</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Video preview modal */}
      {videoPreview && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={() => setVideoPreview(null)}
        >
          <div
            className={`relative rounded-xl overflow-hidden max-w-lg w-full ${isDark ? 'bg-zinc-900' : 'bg-white'} shadow-xl`}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setVideoPreview(null)}
              className="absolute top-2 right-2 z-10 w-8 h-8 rounded-full bg-black/50 flex items-center justify-center text-white hover:bg-black/70"
            >
              <X className="h-4 w-4" />
            </button>
            <video
              src={getPlayableVideoUrl(videoPreview.videoUrl)}
              className="w-full aspect-[9/16] object-contain bg-black"
              controls
              autoPlay
              playsInline
            />
            <div className={`p-3 ${isDark ? 'bg-zinc-900 text-zinc-200' : 'bg-white text-gray-800'}`}>
              <p className="text-sm font-medium">@{videoPreview.creator}</p>
              <p className="text-xs text-zinc-500">
                {formatNumber(videoPreview.views)} views · {formatNumber(videoPreview.likes)} likes
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
